/**
 * useAuth Hook
 * Handles authentication state, token refresh, and session timeout
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { axiosInstance, getIsLoggingOut, setIsLoggingOut } from "../config/api";
import secureStorage from "../utils/secureStorage";

export function useAuth() {
  const [user, setUser] = useState(() => {
    const cachedUser = secureStorage.getItem('user');
    const token = secureStorage.getItem('token');
    if (cachedUser && token) {
      try {
        return JSON.parse(cachedUser);
      } catch {
        return null;
      }
    }
    return null;
  });

  const [loading, setLoading] = useState(() => {
    const cachedUser = secureStorage.getItem('user');
    const token = secureStorage.getItem('token');
    return !(cachedUser && token);
  });

  const handleLogout = useCallback(() => {
    secureStorage.removeItem('token');
    secureStorage.removeItem('user');
    setUser(null);
  }, []);

  const checkAuth = useCallback(() => {
    const token = secureStorage.getItem('token');
    const justLoggedIn = localStorage.getItem('justLoggedIn');
    
    if (justLoggedIn) {
      localStorage.removeItem('justLoggedIn');
      const cachedUser = secureStorage.getItem('user');
      if (cachedUser) {
        try {
          setUser(JSON.parse(cachedUser));
        } catch (e) {
          // Ignore invalid cached data
        }
      }
      setLoading(false);
      return;
    }
    
    if (token) {
      const cachedUser = secureStorage.getItem('user');
      if (cachedUser) {
        try {
          setUser(JSON.parse(cachedUser));
          setLoading(false);
        } catch (e) {
          // Ignore invalid cached data
        }
      }
      
      axiosInstance.get('/auth/me')
        .then(res => {
          setUser(res.data);
          secureStorage.setItem('user', JSON.stringify(res.data));
          console.log('[AUTH] Token verified successfully');
        })
        .catch((err) => {
          if (err.response?.status === 401 && !getIsLoggingOut()) {
            setIsLoggingOut(true);
            console.warn('[AUTH] Token invalid or expired during auth check, clearing session');
            secureStorage.removeItem('token');
            secureStorage.removeItem('user');
            setUser(null);
            if (!window.location.pathname.includes('/login')) {
              window.location.href = '/login?expired=true';
            }
            setTimeout(() => { setIsLoggingOut(false); }, 3000);
          } else if (!err.response) {
            console.warn('[AUTH] Network error during token verification, keeping cached user');
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Token auto-refresh
  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = secureStorage.getItem('token');
      if (!token || !user || getIsLoggingOut()) return;

      try {
        const statusRes = await axiosInstance.get('/auth/token-status');
        const { hours_remaining, needs_refresh, is_critical } = statusRes.data;

        console.log(`[TOKEN] Status: ${hours_remaining?.toFixed(1)}h remaining, needs_refresh=${needs_refresh}`);

        if (needs_refresh || is_critical) {
          console.log('[TOKEN] Refreshing token...');
          const refreshRes = await axiosInstance.post('/auth/refresh-token');
          
          if (refreshRes.data.token) {
            secureStorage.setItem('token', refreshRes.data.token);
            console.log('[TOKEN] Token refreshed successfully!');
            
            if (refreshRes.data.user) {
              secureStorage.setItem('user', JSON.stringify(refreshRes.data.user));
              setUser(refreshRes.data.user);
            }
          }
        }
      } catch (error) {
        if (error.response?.status === 401) {
          console.warn('[TOKEN] Token expired during refresh check. Will redirect on next API call.');
        } else {
          console.warn('[TOKEN] Check/refresh network error:', error.message);
        }
      }
    };

    const initialCheck = setTimeout(checkAndRefreshToken, 5000);
    const intervalCheck = setInterval(checkAndRefreshToken, 4 * 60 * 60 * 1000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[TOKEN] App visible, checking token...');
        checkAndRefreshToken();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(initialCheck);
      clearInterval(intervalCheck);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  // Session timeout - 30 minutes
  useEffect(() => {
    if (!user) return;
    
    const SESSION_TIMEOUT = 30 * 60 * 1000;
    let lastActivity = Date.now();
    
    const updateActivity = () => {
      lastActivity = Date.now();
      localStorage.setItem('lastActivity', lastActivity.toString());
    };
    
    const checkTimeout = () => {
      const storedLastActivity = localStorage.getItem('lastActivity');
      const lastActivityTime = storedLastActivity ? parseInt(storedLastActivity, 10) : lastActivity;
      const timeSinceActivity = Date.now() - lastActivityTime;
      
      if (timeSinceActivity >= SESSION_TIMEOUT) {
        toast.warning("Sesi anda telah tamat tempoh. Sila log masuk semula.", {
          duration: 5000,
        });
        handleLogout();
      }
    };
    
    localStorage.setItem('lastActivity', Date.now().toString());
    
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });
    
    const intervalId = setInterval(checkTimeout, 60 * 1000);
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkTimeout();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, handleLogout]);

  const handleLogin = (userData, token) => {
    secureStorage.setItem('token', token);
    secureStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('justLoggedIn', 'true');
    localStorage.setItem('loginTime', Date.now().toString());
    
    setUser(userData);
    setLoading(false);
    
    console.log('[AUTH] Login successful', { role: userData.role, time: new Date().toISOString() });
    return userData.must_change_password;
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
    secureStorage.setItem('user', JSON.stringify(updatedUser));
  };

  return {
    user,
    loading,
    handleLogin,
    handleLogout,
    handleUserUpdate,
  };
}
